import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   CreditCardIcon,
} from '@forwardflow/icons'
import {
   Button,
   Checkbox,
   ContentCard,
   Grid,
   Group,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import {
   CardElement,
   Elements,
   useElements,
   useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { CheckCircle02 } from '@forwardflow/icons/CheckCircle02'
import { ENV } from '@forwardflow/helpers/configs'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { Tooltip } from '@mantine/core'
import { InfoIcon } from '@forwardflow/assets/svg/InfoIcon'
import { usePricing } from '@forwardflow/modules/onboarding/hooks'
import { isPaymentPricingReady } from '@forwardflow/modules/onboarding/payment-pricing'

const stripePromise = loadStripe(ENV.STRIPE_ID)

const PaymentDetailsForm = () => {
   const {
      getNextStep,
      getPreviousStep,
      onboardingData,
      setIsSubscriptionActivated,
   } = useOnboardingStoreV2()
   const { isSubscriptionActivated, pricingData } = onboardingData
   const {
      seatsCount,
      seatCost,
      maxSpend,
      balanceTopUp,
      chargedToday,
   } = usePricing({ initializeTier: false })
   const [isTermsAccepted, setIsTermsAccepted] = useState(false)
   const [isPolicyAccepted, setIsPolicyAccepted] = useState(false)
   const [isTokenUsagePolicyAccepted, setIsTokenUsagePolicyAccepted] =
      useState(false)
   const [values, setValues] = useState<{
      nameOnCard: string
      city: string
      billingZIPCode: string
      couponCode: string
   }>({
      nameOnCard: '',
      city: '',
      billingZIPCode: '',
      couponCode: '',
   })
   const [isLoading, setIsLoading] = useState(false)
   const stripe = useStripe()
   const elements = useElements()

   const handleSubmit = async () => {
      if (!isPaymentPricingReady(pricingData)) return
      if (isOperatorEmail(onboardingData?.email || '')) {
         getNextStep()
         return
      }
      if (!stripe || !elements || !elements?.getElement(CardElement)) {
         // Stripe.js has not yet loaded.
         // Make sure to disable form submission until Stripe.js has loaded.
         return
      }

      try {
         setIsLoading(true)

         const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements: elements,
            params: {
               billing_details: {
                  name: values.nameOnCard,
                  address: {
                     city: values?.city,
                     postal_code: values?.billingZIPCode,
                  },
               },
            },
         })

         if (error) {
            setIsLoading(false)
            return
         }

         const response = await OnboardingV2Service.subscribeToPackage({
            paymentInfo: {
               cardId:
                  ENV.NODE_ENV === 'development' || ENV.NODE_ENV === 'staging'
                     ? 'pm_card_visa'
                     : paymentMethod.id,
               nameOnCard: values.nameOnCard,
               city: values.city,
               postalCode: values.billingZIPCode,
               country: 'USA',
               addressLineOne: '123 Main St',
            },
            billingPeriod: 'monthly',
            tierId: pricingData.selectedTierId,
            seatsCount: pricingData.seatsCount,
            couponCode: values.couponCode || '',
            newLeadBudget: pricingData?.newLeadBudget,
            reactivateLeadBudget: pricingData?.reactivateLeadBudget,
         })

         if (response?.success) {
            setIsSubscriptionActivated(true)
         }
      } catch (error) {
         // console.error('Error creating payment method', error)
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <Stack gap={48}>
         <Stack gap={16}>
            <motion.div
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, ease: 'easeInOut' }}
               className="rounded-[16px] p-5 border border-[#B17AFF]/[0.5] space-y-4"
               style={{
                  background:
                     'linear-gradient(111deg, rgba(159, 123, 255, 0.10) 1.32%, rgba(235, 105, 194, 0.10) 96.08%)',
               }}
            >
               <div className="flex flex-col md:flex-row gap-0">
                  {/* Monthly Recurring */}
                  <div className="flex-1 flex flex-col md:pr-4 pb-4 md:pb-0">
                     <div className="space-y-3 flex-1">
                        <p className="text-white font-medium">Monthly Recurring</p>
                        <div className="flex items-center justify-between text-sm">
                           <p className="text-white/70">
                              Seats ({seatsCount} {seatsCount === 1 ? 'user' : 'users'})
                           </p>
                           <p className="text-white font-medium">${seatCost.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                           <p className="text-white/70">
                              Recharge Amount
                           </p>
                           <p className="text-white font-medium">${maxSpend.toFixed(2)}</p>
                        </div>
                     </div>
                     <div className="border-t border-white/10 pt-3 mt-3 flex items-center justify-between">
                        <p className="text-white font-medium">Maximum Spend</p>
                        <p className="text-white font-semibold text-lg">
                           ${(seatCost + maxSpend).toFixed(2)}/mo
                        </p>
                     </div>
                  </div>

                  {/* Charged Today */}
                  <div className="flex-1 flex flex-col md:pl-4 md:ml-4 md:-mr-1 -mx-1 md:mx-0 -mb-1 md:-my-1 rounded-xl p-4 bg-white/[0.10] border border-[#B17AFF]/30">
                     <div className="space-y-3 flex-1">
                        <p className="text-white font-medium">Charged Today</p>
                        <div className="flex items-center justify-between text-sm">
                           <p className="text-white/70">
                              Seats ({seatsCount} {seatsCount === 1 ? 'user' : 'users'})
                           </p>
                           <p className="text-white font-medium">${seatCost.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                           <div className="flex items-center gap-1.5">
                              <p className="text-white/70">
                                 Startup Balance
                              </p>
                              <Tooltip
                                 label="This amount will be added to your account balance for lead costs. Unused balance rolls over."
                                 position="top"
                                 withArrow
                                 multiline
                                 w={220}
                              >
                                 <div className="cursor-pointer opacity-50 hover:opacity-80 transition-opacity">
                                    <InfoIcon width={14} height={14} />
                                 </div>
                              </Tooltip>
                           </div>
                           <p className="text-white font-medium">${balanceTopUp.toFixed(2)}</p>
                        </div>
                     </div>
                     <div className="border-t border-white/10 pt-3 mt-3 flex items-center justify-between">
                        <p className="text-white font-medium">Total</p>
                        <p className="text-white font-semibold text-lg">
                           ${chargedToday.toFixed(2)}
                        </p>
                     </div>
                  </div>
               </div>
               <p className="text-white/50 text-xs border-t border-white/10 pt-3">
                  Everything is prepaid. Lead costs are deducted from your
                  balance as you go. Each billing cycle, you'll only be charged
                  the amount needed to refill your balance back to your monthly
                  limit. Unused balance rolls over.
               </p>
            </motion.div>
            {isSubscriptionActivated ? (
               <ContentCard
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-2xl border border-[#00BC7D]/30 bg-[rgba(0,188,125,0.05)]"
               >
                  <Group>
                     <CheckCircle02 color="#00D492" width={32} />
                     <Stack gap={2}>
                        <p className="text-[#D0FAE5]/90 font-medium">
                           Plan Subscription Activated
                        </p>
                     </Stack>
                  </Group>
               </ContentCard>
            ) : (
               <>
                  {' '}
                  <ContentCard
                     className="space-y-4"
                     initial={{ opacity: 0, y: 30 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.6 }}
                  >
                     <Group>
                        <CreditCardIcon width={20} color="#A684FF" />
                        <p className="text-white font-medium">
                           Payment Details
                        </p>
                     </Group>

                     {/* Wrapped CardElement to match your TextInput design */}
                     <div className="relative">
                        <label className="text-white/90 font-medium mb-1.5 block text-sm">
                           Card Information
                        </label>
                        <div className="relative border rounded-lg border-white/20 bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.05] transition-all">
                           <CardElement
                              className="py-2.5 px-3.5"
                              options={{
                                 style: {
                                    base: {
                                       fontSize: '14px',
                                       color: '#9ca3ae',
                                       fontFamily:
                                          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                       '::placeholder': {
                                          color: 'rgba(156, 163, 175, 0.6)',
                                       },
                                       ':focus': {
                                          color: '#ffffff',
                                       },
                                    },
                                    invalid: {
                                       color: '#ef4444',
                                       iconColor: '#ef4444',
                                    },
                                    complete: {
                                       color: '#9ca3ae',
                                    },
                                 },
                                 hidePostalCode: true,
                              }}
                           />
                        </div>
                     </div>

                     <TextInput
                        label="Cardholder Name"
                        placeholder="John Doe"
                        name="nameOnCard"
                        value={values.nameOnCard}
                        onChange={(e) =>
                           setValues({ ...values, nameOnCard: e.target.value })
                        }
                     />

                     <Grid>
                        <Grid.Col span={6}>
                           <TextInput
                              name="city"
                              placeholder="City"
                              label="City"
                              value={values.city}
                              onChange={(e) =>
                                 setValues({ ...values, city: e.target.value })
                              }
                           />
                        </Grid.Col>
                        <Grid.Col span={6}>
                           <TextInput
                              label="Billing ZIP Code"
                              placeholder="eg: 94107"
                              name="billingZIPCode"
                              value={values.billingZIPCode}
                              onChange={(e) => {
                                 const numericValue = e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, 5)
                                 setValues({
                                    ...values,
                                    billingZIPCode: numericValue,
                                 })
                              }}
                           />
                        </Grid.Col>
                     </Grid>

                     <TextInput
                        label="Have a coupon code?"
                        placeholder="eg: FLOW50"
                        name="couponCode"
                        value={values.couponCode}
                        onChange={(e) =>
                           setValues({ ...values, couponCode: e.target.value })
                        }
                     />
                  </ContentCard>
                  <motion.div
                     initial={{ opacity: 0, y: 30 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.9 }}
                     className="space-y-2"
                  >
                     <Group align="center">
                        <Checkbox
                           size="xs"
                           checked={isTermsAccepted}
                           onChange={() => setIsTermsAccepted(!isTermsAccepted)}
                        />
                        <p className="text-white/90 text-sm font-semibold">
                           I agree to the{' '}
                           <a
                              className="text-[#A684FF]"
                              href="https://forward-flow.com/terms"
                              target="_blank"
                           >
                              Terms & Conditions
                           </a>
                        </p>
                     </Group>
                     <Group>
                        <Checkbox
                           size="xs"
                           checked={isPolicyAccepted}
                           onChange={() =>
                              setIsPolicyAccepted(!isPolicyAccepted)
                           }
                        />
                        <p className="text-white/90 text-sm font-semibold">
                           I agree to the{' '}
                           <a
                              className="text-[#A684FF]"
                              href="https://forward-flow.com/privacy"
                              target="_blank"
                           >
                              Privacy policy
                           </a>
                        </p>
                     </Group>
                     <Group>
                        <Checkbox
                           size="xs"
                           checked={isTokenUsagePolicyAccepted}
                           onChange={() =>
                              setIsTokenUsagePolicyAccepted(
                                 !isTokenUsagePolicyAccepted
                              )
                           }
                        />
                        <p className="text-white/90 text-sm font-semibold">
                           I agree to the{' '}
                           <a
                              className="text-[#A684FF]"
                              href="https://forward-flow.com/token-usage-policy"
                              target="_blank"
                           >
                              Token Usage Policy
                           </a>
                        </p>
                     </Group>
                  </motion.div>
               </>
            )}
         </Stack>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.9 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     loading={isLoading}
                     className="w-full"
                     onClick={() => {
                        if (isSubscriptionActivated) {
                           getNextStep()
                        } else {
                           handleSubmit()
                        }
                     }}
                     disabled={
                        isSubscriptionActivated
                           ? false
                           : !isTermsAccepted ||
                             !isPolicyAccepted ||
                             !isTokenUsagePolicyAccepted ||
                             !values.nameOnCard ||
                             !values.city ||
                             (values.billingZIPCode?.trim()?.length ?? 0) !== 5
                     }
                  >
                     {isSubscriptionActivated ? 'Next' : 'Subscribe'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}

/**
 * PaymentDetails component wrapped with Stripe Elements provider.
 * This ensures Stripe context is available only for this specific step.
 */
export const PaymentDetails = ({
   fromAssistant = false,
}: { fromAssistant?: boolean } = {}) => {
   const { onboardingData, goToStep, getNextStep } = useOnboardingStoreV2()
   const navigate = useNavigate()
   const location = useLocation()
   // Keep the return choice through plan review, but not a workspace switch.
   const assistantJourney = fromAssistant || (
      location.state?.onboardingOrigin === 'assistant' &&
      location.state?.organizationId === onboardingData.organizationId
   )

   if (onboardingData.isSubscriptionActivated === true) {
      if (assistantJourney) {
         return (
            <Stack gap={16}>
               <h2 className="text-xl font-semibold">Your subscription is active</h2>
               <p>
                  No additional payment is needed. You can continue onboarding
                  here or return to your existing ChatGPT or Claude conversation.
               </p>
               <Button onClick={() => {
                  getNextStep()
                  navigate('/onboarding', { replace: true, state: null })
               }}>
                  Continue in dashboard <ChevronRightIcon width={18} />
               </Button>
               <p className="text-sm text-white/70">
                  To continue in chat, switch back to the conversation where you
                  started and ask to continue onboarding. Your assistant will
                  check your payment status and resume setup. You can close this tab.
               </p>
            </Stack>
         )
      }
      return (
         <Stack gap={16}>
            <h2 className="text-xl font-semibold">Subscription already active</h2>
            <p>No additional payment is needed for this setup.</p>
            <Button onClick={() => { getNextStep(); navigate('/onboarding') }}>
               Continue setup <ChevronRightIcon width={18} />
            </Button>
         </Stack>
      )
   }

   if (!isPaymentPricingReady(onboardingData.pricingData)) {
      return (
         <Stack gap={16}>
            <h2 className="text-xl font-semibold">Review your plan before payment</h2>
            <p>Your saved plan or budget is incomplete. Confirm your plan, seats and lead budgets before continuing. No payment has been submitted by this page.</p>
            <Button onClick={() => {
               goToStep('plan-selection')
               if (assistantJourney) {
                  navigate('/onboarding', { state: {
                     onboardingOrigin: 'assistant',
                     organizationId: onboardingData.organizationId,
                     reviewPlan: true,
                  } })
               } else {
                  navigate('/onboarding')
               }
            }}>
               Review plan <ChevronRightIcon width={18} />
            </Button>
         </Stack>
      )
   }

   return (
      <Elements stripe={stripePromise}>
         <PaymentDetailsForm />
      </Elements>
   )
}
